<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Sign-in Logs"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="false"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-banner">{{ error }}</div>

      <div class="filters-bar">
        <select v-model="riskFilter" @change="applyFilters" class="filter-select">
          <option value="">All Risk Levels</option>
          <option value="none">None</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <select v-model="statusFilter" @change="applyFilters" class="filter-select">
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
      </div>

      <div v-if="tableState === 'loading'" class="loading-block">Loading sign-in logs...</div>
      <div v-else-if="tableState === 'empty'" class="empty-block">No sign-in logs found for this time period.</div>
      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Application</th>
              <th>Status</th>
              <th>Risk</th>
              <th>Location</th>
              <th>Device</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.signInId" :class="{ 'row-interactive': log.isInteractive, 'row-noninteractive': !log.isInteractive }">
              <td class="date-cell">{{ formatDate(log.createdDateTime) }}</td>
              <td class="user-cell">{{ log.userPrincipalName || log.userId }}</td>
              <td>{{ log.appDisplayName || '--' }}</td>
              <td>
                <span class="status-pill" :class="log.status === 'success' ? 'status-ok' : 'status-bad'">
                  {{ log.status }}
                </span>
              </td>
              <td>
                <span v-if="log.riskLevel && log.riskLevel !== 'none'" class="risk-badge" :class="'risk-' + log.riskLevel">
                  {{ log.riskLevel }}
                </span>
                <span v-else>--</span>
              </td>
              <td class="location-cell">{{ log.city || '--' }}{{ log.country ? ', ' + log.country : '' }}</td>
              <td class="device-cell">{{ log.deviceOS || '--' }}</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getEntraSignInLogs, getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';

export default {
  name: 'EntraSignInLogs',
  components: { EntraPageHeader },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      riskFilter: '', statusFilter: '',
      logs: [], tableState: 'loading',
      page: 1, pageSize: 50, totalPages: 1,
      error: ''
    };
  },
  methods: {
    formatDate: function(ts) {
      if (!ts) return '--';
      try { return new Date(ts).toLocaleString(); } catch(e) { return '--'; }
    },
    applyFilters: function() { this.page = 1; this.loadLogs(); },
    goToPage: function(p) { this.page = p; this.loadLogs(); },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.tableState = 'empty';
        return;
      }
      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || ''; self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || ''; self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || ''; self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});
      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});
      this.loadLogs();
    },
    loadLogs: function() {
      var self = this;
      var params = { page: this.page, pageSize: this.pageSize };
      if (this.riskFilter) params.riskLevel = this.riskFilter;
      if (this.statusFilter) params.status = this.statusFilter;

      getEntraSignInLogs(this.tenantId, params).then(function(data) {
        self.logs = data.items || [];
        self.totalPages = Math.max(1, Math.ceil((data.total || 0) / self.pageSize));
        self.tableState = self.logs.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; background: #F0F9FF; }
.page-body { padding: 20px 24px; }

.error-banner { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #991b1b; font-size: 0.8125rem; margin-bottom: 16px; }

.filters-bar { display: flex; gap: 10px; margin-bottom: 16px; }
.filter-select { padding: 6px 12px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 0.8125rem; color: #334155; background: #ffffff; font-family: 'Inter', sans-serif; }

.loading-block { padding: 48px; text-align: center; color: #94a3b8; }
.empty-block { padding: 48px; text-align: center; color: #94a3b8; font-size: 0.875rem; }

.table-wrapper { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 14px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 9px 14px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.row-interactive { background: #ffffff; }
.row-noninteractive { opacity: 0.7; }
.data-table tbody tr:hover { background: #F0F9FF; }

.date-cell { font-size: 0.75rem; color: #64748b; white-space: nowrap; }
.user-cell { font-weight: 500; color: #0C4A6E; }
.location-cell { font-size: 0.6875rem; color: #64748b; }
.device-cell { font-size: 0.6875rem; color: #64748b; }

.status-pill { font-size: 0.625rem; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; }
.status-ok { background: #dcfce7; color: #047857; }
.status-bad { background: #fee2e2; color: #dc2626; }

.risk-badge { font-size: 0.625rem; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; }
.risk-low { background: #fef3c7; color: #92400e; }
.risk-medium { background: #fed7aa; color: #9a3412; }
.risk-high { background: #fee2e2; color: #dc2626; }

.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px; border-top: 1px solid #e2e8f0; }
.page-btn { background: transparent; border: 1px solid #cbd5e1; padding: 6px 14px; border-radius: 6px; font-size: 0.8125rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.8125rem; color: #64748b; }
</style>
